/**
 * Text, code and Markdown.
 *
 * Two modes, because they are genuinely different jobs: source keeps every
 * byte visible with a gutter and no wrapping, prose sets a measured line length
 * for reading. Markdown opens in prose and switches to source on request; code
 * only ever shows source.
 *
 * Long files are windowed rather than rendered whole — a 200 000-line log
 * inserted into the DOM in one go locks the tab for seconds (§33).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TextModel } from '@moduviewer/handlers';
import { renderMarkdown } from '../lib/markdown.js';

const LINE_HEIGHT = 21.25;      // 12.5px × 1.7, matching viewers.css
const OVERSCAN = 40;

export interface TextViewerProps {
  readonly model: TextModel;
  readonly formatId: string;
  readonly mode: 'source' | 'prose';
  readonly wrap: boolean;
  readonly search: string;
}

export function TextViewer(
  { model, formatId, mode, wrap, search }: TextViewerProps,
): React.JSX.Element {
  if (mode === 'prose' && (formatId === 'markdown' || formatId === 'txt')) {
    return <ProseView text={model.text} markdown={formatId === 'markdown'} />;
  }
  return <SourceView text={model.text} wrap={wrap} search={search} />;
}

function ProseView({ text, markdown }: { text: string; markdown: boolean }): React.JSX.Element {
  const html = useMemo(() => (markdown ? renderMarkdown(text) : null), [text, markdown]);
  if (html === null) {
    return (
      <div className="prose-view" style={{ whiteSpace: 'pre-wrap' }}>
        {text}
      </div>
    );
  }
  // The renderer emits a fixed, escaped subset; no raw HTML from the document
  // ever reaches here (§22, §30).
  return <div className="prose-view" dangerouslySetInnerHTML={{ __html: html }} />;
}

function SourceView(
  { text, wrap, search }: { text: string; wrap: boolean; search: string },
): React.JSX.Element {
  const lines = useMemo(() => text.split('\n'), [text]);
  const scroller = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState({ start: 0, end: Math.min(lines.length, 200) });

  const recompute = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const first = Math.floor(el.scrollTop / LINE_HEIGHT);
    const visible = Math.ceil(el.clientHeight / LINE_HEIGHT);
    setRange({
      start: Math.max(0, first - OVERSCAN),
      end: Math.min(lines.length, first + visible + OVERSCAN),
    });
  }, [lines.length]);

  useEffect(() => {
    recompute();
  }, [recompute]);

  // Wrapping makes line height variable, so the window cannot be computed from
  // a constant — with wrap on, everything is rendered and the file size limit
  // does the protecting instead.
  const windowed = !wrap && lines.length > 400;
  const slice = windowed ? lines.slice(range.start, range.end) : lines;
  const offset = windowed ? range.start : 0;

  return (
    <div
      className="canvas"
      ref={scroller}
      onScroll={windowed ? recompute : undefined}
      style={{ height: '100%' }}
    >
      <div className="text-view" style={windowed ? { height: lines.length * LINE_HEIGHT } : undefined}>
        <div
          className="text-view__gutter"
          style={windowed ? { paddingTop: offset * LINE_HEIGHT + 16 } : undefined}
          aria-hidden="true"
        >
          {slice.map((_, i) => (
            <div key={offset + i}>{offset + i + 1}</div>
          ))}
        </div>
        <pre
          className={`text-view__body${wrap ? ' text-view__body--wrap' : ''}`}
          style={windowed ? { paddingTop: offset * LINE_HEIGHT + 16 } : undefined}
        >
          {slice.map((line, i) => (
            <Line key={offset + i} text={line} search={search} />
          ))}
        </pre>
      </div>
    </div>
  );
}

/** One line, with search hits marked. */
function Line({ text, search }: { text: string; search: string }): React.JSX.Element {
  if (!search) return <div>{text || ' '}</div>;

  const needle = search.toLowerCase();
  const hay = text.toLowerCase();
  const parts: React.JSX.Element[] = [];
  let from = 0;
  let at = hay.indexOf(needle);
  let key = 0;

  while (at >= 0) {
    if (at > from) parts.push(<span key={key++}>{text.slice(from, at)}</span>);
    parts.push(<mark key={key++}>{text.slice(at, at + search.length)}</mark>);
    from = at + search.length;
    at = hay.indexOf(needle, from);
  }
  if (from < text.length) parts.push(<span key={key++}>{text.slice(from)}</span>);
  return <div>{parts.length > 0 ? parts : text || ' '}</div>;
}
